import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SubscriptionSuccess = () => {
  const { session, getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [planId, setPlanId] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await fetch(`${API}/subscription/checkout/status/${sessionId}`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setPlanId(data.plan_id);
          if (data.payment_status === 'paid') {
            setStatus('success');
          } else if (data.status === 'expired') {
            setStatus('expired');
          } else {
            // Poll again in 2s
            setTimeout(checkStatus, 2000);
          }
        } else {
          setStatus('error');
        }
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error('Error checking status:', error);
        setStatus('error');
      }
    };

    checkStatus();
  }, [sessionId]);

  const planName = planId === 'pro' ? 'Pro' : planId === 'basic' ? 'Basic' : planId;

  return (
    <AppLayout maxWidth="max-w-lg">
      <div className="py-12 text-center">
        {status === 'checking' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 className="h-12 w-12 text-teal-400 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-foreground mb-2">Processing your payment...</h1>
            <p className="text-sm text-muted-foreground">This will only take a moment.</p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="w-16 h-16 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2" data-testid="success-title">
              Welcome to {planName}!
            </h1>
            <p className="text-muted-foreground mb-6">
              Your subscription is active. You now have{' '}
              {planId === 'pro' ? 'unlimited' : '10'} Creataby generations per month.
            </p>
            <Link to="/dashboard">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="go-to-dashboard-btn">
                Go to Dashboard
              </Button>
            </Link>
          </motion.div>
        )}

        {(status === 'error' || status === 'expired') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="w-16 h-16 bg-destructive/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="text-xl font-bold text-foreground mb-2">
              {status === 'expired' ? 'Session Expired' : 'Something went wrong'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {status === 'expired'
                ? 'Your checkout session has expired. Please try again.'
                : 'We couldn\'t verify your payment. Please try again or contact support.'}
            </p>
            <Link to="/pricing">
              <Button variant="outline">Back to Pricing</Button>
            </Link>
          </motion.div>
        )}
      </div>
    </AppLayout>
  );
};

export default SubscriptionSuccess;
